import type { CSSProperties, ReactNode, SVGProps } from 'react';
import { PLATFORM_SPECS } from '../../../shared/platforms';
import type { PlatformId } from '../../../shared/types';
import { cn } from '../../lib/utils';

/* ------------------------------------------------------------------ */
/* Glyphs                                                              */
/* ------------------------------------------------------------------ */

/**
 * Hand-drawn 24×24 marks, one per platform, all in `currentColor` so they work
 * on a brand tile (white glyph), on a tinted tile (brand glyph) or bare.
 * Stroked marks use a 2px stroke to match the icon set (lucide) used elsewhere.
 */
const GLYPHS: Record<PlatformId, (props: SVGProps<SVGSVGElement>) => ReactNode> = {
  facebook: (props) => (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <path
        d="M13.6 21.9v-8.2h2.77l.42-3.21H13.6V8.44c0-.93.26-1.56 1.6-1.56h1.7V4.01c-.29-.04-1.3-.13-2.47-.13-2.45 0-4.12 1.49-4.12 4.24v2.37H7.53v3.21h2.78v8.2h3.29Z"
        fill="currentColor"
      />
    </svg>
  ),
  instagram: (props) => (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <rect
        x="3.2"
        y="3.2"
        width="17.6"
        height="17.6"
        rx="5"
        stroke="currentColor"
        strokeWidth="2"
      />
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2" />
      <circle cx="17.1" cy="6.9" r="1.25" fill="currentColor" />
    </svg>
  ),
  x: (props) => (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <path
        d="M17.9 3h2.94l-6.42 7.34L22 21h-5.92l-4.63-6.06L6.15 21H3.2l6.87-7.85L2.5 3h6.07l4.19 5.54L17.9 3Zm-1.03 16.23h1.63L7.2 4.68H5.45l11.42 14.55Z"
        fill="currentColor"
      />
    </svg>
  ),
  linkedin: (props) => (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <path
        d="M6.94 9.02v9.6H3.6v-9.6h3.34Zm.23-3.06a1.9 1.9 0 1 1-3.8 0 1.9 1.9 0 0 1 3.8 0Zm13.23 12.66h-3.33v-5.03c0-1.26-.45-2.12-1.58-2.12-.86 0-1.37.58-1.6 1.14-.08.2-.1.48-.1.76v5.25h-3.34s.04-8.52 0-9.6h3.33v1.36c.45-.68 1.24-1.66 3.02-1.66 2.2 0 3.6 1.44 3.6 4.52v5.38Z"
        fill="currentColor"
      />
    </svg>
  ),
  wordpress: (props) => (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="m4.6 8.4 3.3 8.7 1.86-5.3M11.35 8.4l3.3 8.7 1.9-5.55c.36-1.06.4-1.9.05-2.6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M9.76 11.8 8.4 8.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
  tiktok: (props) => (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <path
        d="M16.4 3h-2.72v12.3a2.32 2.32 0 1 1-1.9-2.28V10.2a5.24 5.24 0 1 0 4.62 5.2V9.6a6.5 6.5 0 0 0 3.6 1.1V7.98A3.6 3.6 0 0 1 16.4 4.4V3Z"
        fill="currentColor"
      />
    </svg>
  ),
  youtube: (props) => (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <rect
        x="2.2"
        y="5.4"
        width="19.6"
        height="13.2"
        rx="4.2"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path d="M10.3 9.2 15.2 12l-4.9 2.8V9.2Z" fill="currentColor" />
    </svg>
  ),
};

export interface PlatformGlyphProps extends SVGProps<SVGSVGElement> {
  platform: PlatformId;
}

/**
 * The bare mark in `currentColor`. Use it when the surrounding element already
 * sets a color (previews, list rows); `PlatformIcon` wraps it in a brand tile.
 */
export function PlatformGlyph({ platform, className, ...props }: PlatformGlyphProps) {
  const Glyph = GLYPHS[platform];
  return <>{Glyph({ className: cn('size-full', className), 'aria-hidden': true, ...props })}</>;
}

/* ------------------------------------------------------------------ */
/* Tiles & chips                                                       */
/* ------------------------------------------------------------------ */

const SIZES = {
  xs: { box: 'size-5 rounded', pad: 'p-0.5' },
  sm: { box: 'size-6 rounded-md', pad: 'p-1' },
  md: { box: 'size-8 rounded-lg', pad: 'p-1.5' },
  lg: { box: 'size-10 rounded-lg', pad: 'p-2' },
  xl: { box: 'size-14 rounded-xl', pad: 'p-3' },
} as const;

export type PlatformIconSize = keyof typeof SIZES;

/** Perceived brightness of a `#rrggbb` color, 0 (black) to 1 (white). */
function luminance(hex: string): number {
  const value = hex.replace('#', '');
  if (value.length !== 6) return 0.5;
  const [r, g, b] = [0, 2, 4].map((offset) => Number.parseInt(value.slice(offset, offset + 2), 16) / 255);
  return 0.2126 * (r ?? 0) + 0.7152 * (g ?? 0) + 0.0722 * (b ?? 0);
}

/**
 * Brand colors reach the DOM as custom properties so `dark:` can swap them.
 *
 * A near-black brand (X) disappears against the dark surface, so solid tiles
 * flip to the lifted brand color with a dark mark — the only place the palette
 * is allowed to deviate from the published hex.
 */
function brandVars(platform: PlatformId): CSSProperties {
  const spec = PLATFORM_SPECS[platform];
  const invertOnDark = luminance(spec.brandColor) < 0.12;
  return {
    '--pap-brand': spec.brandColor,
    '--pap-brand-dark': spec.brandColorDark,
    '--pap-brand-fg': spec.brandForeground,
    '--pap-brand-solid-dark': invertOnDark ? spec.brandColorDark : spec.brandColor,
    '--pap-brand-solid-fg-dark': invertOnDark ? spec.brandColor : spec.brandForeground,
  } as CSSProperties;
}

export interface PlatformIconProps {
  platform: PlatformId;
  size?: PlatformIconSize;
  /**
   * `solid` — brand-filled tile with a white mark (connection cards, previews).
   * `soft`  — brand-tinted tile with a brand-colored mark (chips, list rows).
   * `plain` — no tile, just the mark in the brand color.
   */
  variant?: 'solid' | 'soft' | 'plain';
  className?: string;
}

/**
 * Brand tile for a platform. Brand colors appear only here and in the composer
 * previews — the rest of the app stays on the neutral + accent scale.
 */
export function PlatformIcon({
  platform,
  size = 'sm',
  variant = 'solid',
  className,
}: PlatformIconProps) {
  const spec = PLATFORM_SPECS[platform];
  const dimensions = SIZES[size];

  if (variant === 'plain') {
    return (
      <span
        title={spec.name}
        style={brandVars(platform)}
        className={cn(
          'inline-flex shrink-0 text-[var(--pap-brand)] dark:text-[var(--pap-brand-dark)]',
          dimensions.box,
          className,
        )}
      >
        <PlatformGlyph platform={platform} />
      </span>
    );
  }

  return (
    <span
      title={spec.name}
      style={brandVars(platform)}
      className={cn(
        'inline-flex shrink-0 items-center justify-center',
        dimensions.box,
        dimensions.pad,
        variant === 'solid'
          ? 'bg-[var(--pap-brand)] text-[var(--pap-brand-fg)] ring-1 ring-black/5 dark:bg-[var(--pap-brand-solid-dark)] dark:text-[var(--pap-brand-solid-fg-dark)] dark:ring-white/10'
          : 'bg-[color-mix(in_oklab,var(--pap-brand)_16%,transparent)] text-[var(--pap-brand)] dark:bg-[color-mix(in_oklab,var(--pap-brand-dark)_20%,transparent)] dark:text-[var(--pap-brand-dark)]',
        className,
      )}
    >
      <PlatformGlyph platform={platform} />
    </span>
  );
}

export interface PlatformChipProps {
  platform: PlatformId;
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  /** Trailing content: a char counter, a status dot, a remove button. */
  trailing?: ReactNode;
  className?: string;
}

/** Selectable platform chip used by the composer's platform picker. */
export function PlatformChip({
  platform,
  selected,
  disabled,
  onClick,
  trailing,
  className,
}: PlatformChipProps) {
  const spec = PLATFORM_SPECS[platform];
  const interactive = Boolean(onClick) && !disabled;

  return (
    <button
      type="button"
      disabled={disabled || !onClick}
      aria-pressed={onClick ? Boolean(selected) : undefined}
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-2 rounded-full border py-1 pl-1 pr-3 text-sm font-medium',
        'transition-[border-color,background-color,color] duration-150',
        selected
          ? 'border-accent/60 bg-accent-soft text-fg'
          : 'border-border text-muted',
        interactive && !selected && 'hover:border-border-strong hover:text-fg',
        disabled && 'cursor-not-allowed opacity-50',
        !onClick && 'cursor-default',
        className,
      )}
    >
      <PlatformIcon platform={platform} size="sm" variant={selected ? 'solid' : 'soft'} />
      {spec.shortName}
      {spec.comingSoon && (
        <span className="rounded-full bg-elevated px-1.5 py-px text-[10px] uppercase tracking-wide text-subtle">
          soon
        </span>
      )}
      {trailing}
    </button>
  );
}
